import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { useLocale } from "@/lib/locale";

export default function Settings() {
    const { user } = useAuth();
    const { t } = useLocale();
    const [limits, setLimits] = useState(null);

    useEffect(() => {
        api.get("/plan/limits").then(({ data }) => setLimits(data)).catch(() => {});
    }, []);

    const tierLabel = (tier) => ({ free: t("settings.plan_free"), echo: "Echo", legacy: "Legacy" }[tier] || tier);
    const expires = limits?.plan_expires_at ? new Date(limits.plan_expires_at) : null;

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-24 animate-fade-in">
                <p className="label-eyebrow mb-3">{t("settings.eyebrow")}</p>
                <h1 className="h-serif text-4xl md:text-5xl mb-12">{t("settings.title")}</h1>

                <div className="border-t border-ink-border py-6">
                    <p className="label-eyebrow">{t("settings.signed_as")}</p>
                    <p className="font-serif text-2xl mt-2">{user?.name}</p>
                    <p className="text-ink-textMid">{user?.email}</p>
                </div>

                <div className="border-t border-ink-border py-6">
                    <p className="label-eyebrow">{t("settings.auth_method")}</p>
                    <p className="font-serif text-xl mt-2 capitalize">{user?.auth_provider}</p>
                </div>

                <div className="border-t border-ink-border py-6">
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                            <p className="label-eyebrow">{t("settings.plan")}</p>
                            <p className="font-serif text-3xl mt-2">{limits ? tierLabel(limits.plan_tier) : "…"}</p>
                            {expires && (
                                <p className="text-ink-textLow text-sm mt-2">
                                    {t("settings.plan_valid")} {expires.toLocaleDateString()}
                                </p>
                            )}
                            {limits && (
                                <div className="mt-4 space-y-1 text-sm text-ink-textMid">
                                    <p>{t("settings.plan_presences")}: {limits.used_presences}/{limits.max_presences}</p>
                                    <p>{t("settings.plan_messages")}: {limits.used_messages_this_month}/{limits.max_messages_per_month}</p>
                                </div>
                            )}
                        </div>
                        <Link to="/upgrade" className="btn-primary" data-testid="settings-upgrade">
                            {limits?.plan_tier === "free" ? t("settings.plan_explore") : t("settings.plan_manage")}
                        </Link>
                    </div>
                </div>

                {limits?.features && (
                    <div className="border-t border-ink-border py-6">
                        <p className="label-eyebrow">{t("settings.features")}</p>
                        <ul className="mt-4 space-y-2 text-ink-textHigh">
                            <li>{t("settings.feat_voice")}: <span className={limits.features.voice ? "text-ink-gold" : "text-ink-textLow"}>{limits.features.voice ? t("settings.feat_yes_soon") : t("settings.feat_no")}</span></li>
                            <li>{t("settings.feat_photo")}: <span className={limits.features.talking_photo ? "text-ink-gold" : "text-ink-textLow"}>{limits.features.talking_photo ? t("settings.feat_yes_soon") : t("settings.feat_no")}</span></li>
                            <li>{t("settings.feat_unsolicited")}: <span className={limits.features.unsolicited ? "text-ink-gold" : "text-ink-textLow"}>{limits.features.unsolicited ? t("settings.feat_yes") : t("settings.feat_no")}</span></li>
                        </ul>
                    </div>
                )}

                <div className="border-t border-ink-border py-6" data-testid="settings-privacy-area">
                    <p className="label-eyebrow">{t("privacy.title")}</p>
                    <p className="text-ink-textMid mt-2 mb-4">{t("privacy.intro")}</p>
                    <Link
                        to="/settings/privacy"
                        className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold"
                        data-testid="settings-privacy-link"
                    >
                        {t("privacy.eyebrow")} →
                    </Link>
                </div>

                <div className="border-t border-ink-border py-6" data-testid="settings-legal">
                    <p className="label-eyebrow">{t("settings.legal")}</p>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <Link to="/legal/terms" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-terms-link">{t("legal.nav_terms")}</Link>
                        <Link to="/legal/privacy" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-privacy-policy-link">{t("legal.nav_privacy")}</Link>
                        <Link to="/legal/biometric" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-biometric-link">{t("legal.nav_biometric")}</Link>
                        <Link to="/legal/ai-notice" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-ai-notice-link">{t("legal.nav_ai_notice")}</Link>
                        <Link to="/legal/memorial-policy" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-memorial-link">{t("legal.nav_memorial")}</Link>
                    </div>
                </div>

                <div className="border-t border-ink-border py-6">
                    <p className="label-eyebrow">{t("settings.safety")}</p>
                    <p className="text-ink-textMid mt-2 mb-3">{t("settings.safety_body")}</p>
                    <Link to="/legal/report" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/50 hover:decoration-ink-gold" data-testid="settings-report-link">{t("settings.safety_link")}</Link>
                </div>
            </main>
        </div>
    );
}
